import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import MyTrainingHome from './MyTrainingHome';
import RosterOverview from './coaching/RosterOverview';
import Account from './Account';
import UnitPreference from './UnitPreference';
import CoachInvitationsCard from './CoachInvitationsCard';
import { audienceFrom, listFrom, AUDIENCE } from './audience';
import '../styles/Home.css';

// The redundant auth guard that used to live here — a useEffect redirecting to
// /login when !isAuthenticated — is gone. App.js already wraps this route in
// PrivateRoute, which redirects before the component renders. The effect ran
// *after* the first paint, so it could never prevent anything PrivateRoute
// doesn't already prevent; all it added was a second redirect path to reason
// about. Guard routes in one place.

// # WHY THIS RENDERS THE OTHER HOME RATHER THAN REDIRECTING TO IT
//
// An athlete landed here — on Exercise library, Programs and Account — while the
// screen built for them sat at /training. Three coach-shaped cards and nothing
// about what they train today.
//
// A redirect would have been fewer lines and would have taken /home away from
// them: click Home in the nav and get bounced somewhere else, with no way to see
// the page the link names. Rendering means the URL keeps its meaning for
// everybody, every existing link still works, and the decision lives in one
// place rather than at the five call sites that navigate here.
const Home = () => {
  const { username, token } = useAuth();

  // null while unknown. Rendering the coach cards and then swapping them would
  // flash the wrong home at the person this change is for.
  const [audience, setAudience] = useState(null);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [roster, assigned] = await Promise.all([
          axios.get(buildApiUrl('/athletes'), authConfig()),
          axios.get(buildApiUrl('/assigned'), authConfig()),
        ]);
        if (!live) return;
        setAudience(audienceFrom({
          athletes: listFrom(roster.data, 'athletes'),
          assignments: listFrom(assigned.data, 'assignments'),
        }));
      } catch {
        // A failed lookup must not leave a blank screen. Follows audienceFrom's
        // brand-new-account default, which moved to ATHLETE once the athlete
        // home gained "Write me a program" — otherwise a transient failure on
        // /athletes would still land a new user on coach cards no matter what
        // that function says.
        if (live) setAudience(AUDIENCE.ATHLETE);
      }
    })();
    return () => { live = false; };
  }, [authConfig]);

  if (audience === null) {
    return (
      <BaseLayout>
        <div className="home">
          <p className="meta-copy" role="status">Loading your home…</p>
        </div>
      </BaseLayout>
    );
  }

  if (audience === AUDIENCE.ATHLETE) return <MyTrainingHome />;

  return (
    <BaseLayout>
      <div className="home">
        <header className="home-header">
          <span className="pill">Dashboard</span>
          {/*
            The fallback used to be `username || 'lets get to work'`, which
            rendered the sentence "Welcome, lets get to work!" — a placeholder
            that shipped. A missing username is now just a shorter greeting.
          */}
          <h1 className="page-title home-title">
            {username ? `Welcome back, ${username}` : 'Welcome back'}
          </h1>
          <p className="meta-copy home-subtitle">Pick up where you left off.</p>
        </header>

        {/* A coach can be invited by another coach, and this is their home —
            without it an invitation to anybody who already has a roster
            would have nowhere to be answered. First, because it answers a
            question only this coach can answer, ahead of the roll-up below
            which is about the roster they already have. */}
        <CoachInvitationsCard />

        {/*
          The worklist, above everything else on the page.

          This home used to be three cards — Exercise library, Programs, Account
          — none of which said anything about the people the coach actually
          trains. The roll-up that does exist lived on /coaching, so "is anybody
          overdue this morning" cost a nav click and a scroll past three admin
          forms, every single day.

          A sibling of .home-grid rather than a card inside it: surfaces.css
          forbids nesting .card in .card, and every child of the grid is already
          one.

          It renders nothing when the roll-up comes back empty. That should be
          unreachable — audienceFrom only returns COACH when the roster is
          non-empty — so an empty roll-up here means a partial failure rather
          than a state, and the cards below stay usable either way.
        */}
        <RosterOverview />

        <div className="home-grid">
          <article className="card card-interactive home-card">
            <h2 className="section-title home-card-title">Exercise library</h2>
            <p className="body-copy home-card-text">
              Browse every movement in the library, filtered by pattern, with demo video.
            </p>
            <Link to="/exercises" className="btn-primary home-card-action">
              Browse exercises
            </Link>
          </article>

          <article className="card card-interactive home-card">
            <h2 className="section-title home-card-title">Programs</h2>
            <p className="body-copy home-card-text">
              Build a training block, prescribe sets, tempo and intensity, then log your sessions.
            </p>
            <Link to="/create-program" className="btn-primary home-card-action">
              Open programs
            </Link>
          </article>

          <article className="card home-card home-card-account">
            <h2 className="section-title home-card-title">Account</h2>
            {/*
              This card claimed to "manage your account settings and
              preferences" and offered only a logout button, because the profile
              endpoints existed and were mounted nowhere. They are mounted now,
              so the card finally does what it always said.

              Here rather than behind a nav link of its own. The card already
              held the unit preference and the logout button, so this is where
              somebody goes looking for their account — and the desktop nav row
              is still asserted not to wrap (E2E-NAV-1), which a seventh link
              would put back in play even now that a phone gets a real menu.
            */}
            {/*
              Two columns from 640px up, because this card now spans the whole
              grid row. Full width and a single column would have meant 1100px
              password fields at 1280 — a worse read than the tall narrow card
              this replaced, and the same empty space moved from below the card
              to beside it. A div rather than a section: a section with a
              heading is a landmark, and this is a column, not a region.
            */}
            <div className="home-account-columns">
              <Account />
              <div>
                <h3 className="kicker home-card-kicker">Load units</h3>
                <p className="meta-copy home-card-text">
                  How loads are shown and entered. Your history is stored the same way
                  either way, so switching is safe at any time.
                </p>
                <UnitPreference />
              </div>
            </div>
          </article>
        </div>
      </div>
    </BaseLayout>
  );
};

export default Home;
